import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { PERFIL_COPY, PerfilSeccionId } from '../../../data/perfil-microempresario.content';
import { PerfilDocumentosService } from '../../documentos/perfil-documentos.service';
import { PerfilSegmento } from '../../segmento/perfil-segmento.util';
import { AnalyticsTagService } from 'src/app/services/analytics-tag.service';

export type PerfilIntroIcon =
  | 'id'
  | 'clipboard'
  | 'people'
  | 'location'
  | 'document'
  | 'photos'
  | 'social'
  | 'accounts'
  | 'supports'
  | 'tax';

type IntroModalPart = { text: string; em?: boolean };
type IntroModalItem = {
  icon: PerfilIntroIcon;
  parts: ReadonlyArray<IntroModalPart>;
};
type IntroModalCopy = {
  titulo: string;
  intro: string;
  items: ReadonlyArray<IntroModalItem>;
};

/**
 * Modal intro reutilizable por sección (Sobre ti / negocio / ingresos).
 * Ingresos PN varía Express/Tradicional; PJ usa variantes *-juridica.
 * El tip de fotos se agrega solo si el catálogo tiene fotos en esa sección.
 */
@Component({
  selector: 'app-perfil-intro-modal',
  templateUrl: './perfil-intro-modal.component.html',
  styleUrls: ['./perfil-intro-modal.component.scss'],
  standalone: false,
})
export class PerfilIntroModalComponent implements OnChanges, OnDestroy {
  @Input() open = false;
  @Input() seccionId: PerfilSeccionId = 'sobre-ti';
  @Input() segmento: PerfilSegmento = 'tradicional';
  @Input() juridica = false;

  @Output() closed = new EventEmitter<void>();
  @Output() completarDatos = new EventEmitter<PerfilSeccionId>();

  readonly copy = PERFIL_COPY;
  /** Fotos parametrizadas en la sección actual (catálogo). */
  private seccionTieneFotos = false;
  private cantidadFotos = 0;
  private docsSub?: Subscription;

  constructor(
    private readonly analyticsTag: AnalyticsTagService,
    private readonly documentosService: PerfilDocumentosService
  ) {}

  private readonly iconSrcMap: Record<PerfilIntroIcon, string> = {
    id: 'assets/img/perfil/icon-intro-documento.svg',
    clipboard: 'assets/img/perfil/icon-intro-seguridad-social.svg',
    people: 'assets/img/perfil/icon-intro-entorno.svg',
    location: 'assets/img/perfil/icon-intro-ubicacion.svg',
    document: 'assets/img/perfil/icon-intro-camara-rut.svg',
    photos: 'assets/img/perfil/icon-intro-fotos.svg',
    social: 'assets/img/perfil/icon-intro-redes.svg',
    accounts: 'assets/img/perfil/icon-intro-cuentas.svg',
    supports: 'assets/img/perfil/icon-intro-soportes.svg',
    tax: 'assets/img/perfil/icon-intro-renta.svg',
  };

  get modal(): IntroModalCopy {
    const base = this.resolveBaseModal();
    const items: IntroModalItem[] = base.items.filter(
      (item) => item.icon !== 'photos'
    );
    if (this.seccionTieneFotos) {
      items.push(this.buildPhotosItem(this.cantidadFotos));
    }
    return { ...base, items };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['open'] ||
      changes['seccionId'] ||
      changes['juridica'] ||
      changes['segmento']
    ) {
      if (this.open) {
        this.refreshFotosFromCatalog();
      }
    }
  }

  ngOnDestroy(): void {
    this.docsSub?.unsubscribe();
  }

  iconSrc(icon: PerfilIntroIcon): string {
    return this.iconSrcMap[icon];
  }

  onClose(): void {
    this.closed.emit();
  }

  onCompletar(): void {
    this.analyticsTag.trackCtaClick({
      ctaLabel: this.copy.modalIntro.ctaCompletarDatos,
      ctaSection: 'perfil-intro-modal',
      ctaComponent: 'perfil-intro-modal',
      ctaElementId: `btn-perfil-intro-completar-${this.seccionId}`,
      hu: '8099',
    });
    this.completarDatos.emit(this.seccionId);
  }

  onBackdrop(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  private resolveBaseModal(): IntroModalCopy {
    if (this.juridica) {
      if (this.seccionId === 'sobre-ti') {
        return this.copy.modalIntro['sobre-ti-juridica'] as unknown as IntroModalCopy;
      }
      if (this.seccionId === 'sobre-ingresos') {
        return this.copy.modalIntro[
          'sobre-ingresos-juridica'
        ] as unknown as IntroModalCopy;
      }
    }
    if (this.seccionId === 'sobre-ingresos' && this.segmento === 'express') {
      return this.copy.modalIntro[
        'sobre-ingresos-express'
      ] as unknown as IntroModalCopy;
    }
    return this.copy.modalIntro[this.seccionId] as unknown as IntroModalCopy;
  }

  private refreshFotosFromCatalog(): void {
    this.docsSub?.unsubscribe();
    this.docsSub = this.documentosService.ensureLoaded().subscribe((docs) => {
      const fotos = docs.filter(
        (d) => d.seccionId === this.seccionId && !!d.esFoto
      );
      this.cantidadFotos = fotos.length;
      this.seccionTieneFotos = fotos.length > 0;
    });
  }

  private buildPhotosItem(count: number): IntroModalItem {
    const n = count > 0 ? count : 4;
    const fotoLabel = n === 1 ? '1 foto' : `${n} fotos`;
    return {
      icon: 'photos',
      parts: [
        { text: 'Tu celular:', em: true },
        { text: ` Necesitarás tomar o subir ${fotoLabel} de tu negocio.` },
      ],
    };
  }
}
